export const ELEVENLABS_BROWSER_SDK_VERSION = '1.25.0' as const;

export const VOICE_CLIENT_TOOL_NAMES = [
  'validate_booking_candidate',
  'start_sms_verification',
  'wait_for_sms_verification',
] as const;

export type VoiceClientToolName = (typeof VOICE_CLIENT_TOOL_NAMES)[number];

export const VOICE_CLIENT_TOOL_FAILURE_CODES = {
  client_exception: ['client_tool_exception'],
  network_error: ['booking_validate_network'],
  http_error: ['booking_validate_http_4xx', 'booking_validate_http_5xx'],
  timeout: ['booking_validate_timeout'],
  malformed_response: ['booking_validate_malformed'],
  provider_cancelled: ['provider_disconnected'],
  client_cancelled: ['client_ended'],
} as const;

export type VoiceClientToolErrorType = keyof typeof VOICE_CLIENT_TOOL_FAILURE_CODES;
export type VoiceClientToolMessageCode =
  (typeof VOICE_CLIENT_TOOL_FAILURE_CODES)[VoiceClientToolErrorType][number];

export type VoiceClientToolDiagnostic = {
  sequence: number;
  toolName: VoiceClientToolName;
  outcome: 'success' | 'error' | 'cancelled';
  durationMs: number;
  errorType?: VoiceClientToolErrorType;
  messageCode?: VoiceClientToolMessageCode;
};

type SafeFailure = {
  [ErrorType in VoiceClientToolErrorType]: {
    errorType: ErrorType;
    messageCode: (typeof VOICE_CLIENT_TOOL_FAILURE_CODES)[ErrorType][number];
  };
}[VoiceClientToolErrorType];

type ActiveExecution = {
  controller: AbortController;
  startedAt: number;
  toolName: VoiceClientToolName;
  cancelled: boolean;
};

const MAX_DIAGNOSTICS = 20;
const MAX_DURATION_MS = 300_000;

export class VoiceClientToolError extends Error {
  readonly errorType: VoiceClientToolErrorType;
  readonly messageCode: VoiceClientToolMessageCode;

  constructor(failure: SafeFailure) {
    super(failure.messageCode);
    this.name = 'VoiceClientToolError';
    this.errorType = failure.errorType;
    this.messageCode = failure.messageCode;
  }
}

export function classifyVoiceClientToolError(error: unknown): SafeFailure {
  if (error instanceof VoiceClientToolError) {
    return { errorType: error.errorType, messageCode: error.messageCode } as SafeFailure;
  }
  return {
    errorType: 'client_exception',
    messageCode: 'client_tool_exception',
  };
}

export async function fetchVoiceClientToolJson(
  input: RequestInfo | URL,
  init: RequestInit,
  sessionSignal: AbortSignal,
  timeoutMs: number,
  fetcher: typeof fetch = globalThis.fetch
): Promise<unknown> {
  if (sessionSignal.aborted) {
    throw new VoiceClientToolError({
      errorType: 'provider_cancelled',
      messageCode: 'provider_disconnected',
    });
  }
  const controller = new AbortController();
  let timedOut = false;
  const abortForSession = () => controller.abort();
  sessionSignal.addEventListener('abort', abortForSession, { once: true });
  const timeout = globalThis.setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  let response: Response;
  try {
    response = await fetcher(input, { ...init, signal: controller.signal });
  } catch {
    if (sessionSignal.aborted) {
      throw new VoiceClientToolError({
        errorType: 'provider_cancelled',
        messageCode: 'provider_disconnected',
      });
    }
    if (timedOut) {
      throw new VoiceClientToolError({
        errorType: 'timeout',
        messageCode: 'booking_validate_timeout',
      });
    }
    throw new VoiceClientToolError({
      errorType: 'network_error',
      messageCode: 'booking_validate_network',
    });
  } finally {
    globalThis.clearTimeout(timeout);
    sessionSignal.removeEventListener('abort', abortForSession);
  }

  if (!response.ok) {
    throw new VoiceClientToolError({
      errorType: 'http_error',
      messageCode:
        response.status >= 500 ? 'booking_validate_http_5xx' : 'booking_validate_http_4xx',
    });
  }

  try {
    return await response.json();
  } catch {
    throw new VoiceClientToolError({
      errorType: 'malformed_response',
      messageCode: 'booking_validate_malformed',
    });
  }
}

function boundedDuration(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 0;
  return Math.min(MAX_DURATION_MS, Math.round(value));
}

export class VoiceClientToolDiagnostics {
  private readonly active = new Set<ActiveExecution>();
  private readonly observations: VoiceClientToolDiagnostic[] = [];
  private sequence = 0;

  constructor(private readonly now: () => number = () => performance.now()) {}

  async run<T>(
    toolName: VoiceClientToolName,
    handler: (signal: AbortSignal) => T | Promise<T>,
    recover?: (failure: SafeFailure) => T | Promise<T>
  ): Promise<T> {
    const execution: ActiveExecution = {
      controller: new AbortController(),
      startedAt: this.now(),
      toolName,
      cancelled: false,
    };
    this.active.add(execution);

    try {
      const result = await handler(execution.controller.signal);
      if (!execution.cancelled) {
        this.record({
          toolName,
          outcome: 'success',
          durationMs: this.now() - execution.startedAt,
        });
      }
      return result;
    } catch (error) {
      const failure = classifyVoiceClientToolError(error);
      if (!execution.cancelled) {
        this.record({
          toolName,
          outcome: failure.errorType.endsWith('cancelled') ? 'cancelled' : 'error',
          durationMs: this.now() - execution.startedAt,
          ...failure,
        });
      }
      if (recover) {
        return recover(failure);
      }
      // Never allow lead data or raw connector errors to reach the SDK's
      // client_tool_result payload.
      throw new VoiceClientToolError(failure);
    } finally {
      this.active.delete(execution);
    }
  }

  cancelPending(
    failure:
      | Extract<SafeFailure, { errorType: 'provider_cancelled' }>
      | Extract<SafeFailure, { errorType: 'client_cancelled' }>
  ): void {
    for (const execution of this.active) {
      if (execution.cancelled) continue;
      execution.cancelled = true;
      this.record({
        toolName: execution.toolName,
        outcome: 'cancelled',
        durationMs: this.now() - execution.startedAt,
        ...failure,
      });
      execution.controller.abort();
    }
  }

  snapshot(): VoiceClientToolDiagnostic[] {
    return this.observations.map((item) => ({ ...item }));
  }

  reset(): void {
    for (const execution of this.active) {
      execution.cancelled = true;
      execution.controller.abort();
    }
    this.active.clear();
    this.observations.length = 0;
    this.sequence = 0;
  }

  private record(input: Omit<VoiceClientToolDiagnostic, 'sequence'>): void {
    this.sequence += 1;
    this.observations.push({
      ...input,
      sequence: this.sequence,
      durationMs: boundedDuration(input.durationMs),
    });
    if (this.observations.length > MAX_DIAGNOSTICS) {
      this.observations.splice(0, this.observations.length - MAX_DIAGNOSTICS);
    }
  }
}
